import {useEffect, useRef, useState} from 'react';

import {searchUsersActiveProducts} from 'src/openapi/admin/apis/ProductsApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';

type Props = {userIds: number[], cacheKey: string, enabled: boolean, refreshVersion: number};
type Access = Record<string, string[]>;
type Result = {key: string, data?: Access, error?: ApiError};

export const useUserProductAccess = ({userIds, cacheKey, enabled, refreshVersion}: Props) => {
    const apiDispatch = useAppDispatch();
    const cache = useRef(new Map<string, Access>());
    const [result, setResult] = useState<Result>();
    const idsKey = JSON.stringify(userIds);
    const key = JSON.stringify([refreshVersion, cacheKey, idsKey]);
    const shouldLoad = enabled && userIds.length > 0;

    useEffect(() => {
        if (!shouldLoad || cache.current.has(key)) {
            return;
        }
        const controller = new AbortController();
        const load = async () => {
            try {
                const response = await apiDispatch(searchUsersActiveProducts({
                    usersActiveProductsSearchRequest: {userIds: JSON.parse(idsKey)},
                }, {signal: controller.signal}));
                if (controller.signal.aborted) {
                    return;
                }
                if (response.payload.success) {
                    cache.current.set(key, response.payload.data);
                    if (cache.current.size > 20) {
                        cache.current.delete(cache.current.keys().next().value!);
                    }
                    setResult({key, data: response.payload.data});
                } else {
                    setResult({key, error: response.payload.data});
                }
            } catch {
                if (!controller.signal.aborted) {
                    setResult({key, error: {message: 'Failed to load product access'}});
                }
            }
        };
        setResult(undefined);
        load();
        return () => controller.abort();
    }, [apiDispatch, shouldLoad, key, idsKey]);

    const data = shouldLoad ? cache.current.get(key) : undefined;
    const error = shouldLoad && result?.key === key ? result.error : undefined;
    return {data, error, loading: shouldLoad && !data && !error};
};
